import { create } from "zustand";

/**
 * Async dispatch store for the "you are about to send an unanonymized turn
 * on a thread that was previously anonymized" confirmation gate. The check
 * lives in the chat adapter (single point that covers Send button, Enter key,
 * and any programmatic invocation of `composer.send()`); the adapter calls
 * {@link request} and awaits the returned Promise.
 *
 * `pending.resolve` is a non-serializable closure, which is why this lives in
 * its own store rather than on the broader `useAnonymizationStore` — the
 * adapter's per-thread `acknowledgedUnanonymizedSend` persistence and the
 * modal's transient pending-request slot have different lifetimes and
 * different cloneability constraints. Keeping them split makes it impossible
 * to accidentally try to serialize `pending` to localStorage.
 *
 * A second {@link request} arriving while another is in flight is treated as
 * a logic error (two concurrent sends shouldn't be possible for the same
 * thread under the existing runtime semantics), but to be safe we resolve any
 * prior pending request with `false` (= cancel) before replacing it, so the
 * earlier adapter run unblocks cleanly rather than hanging forever.
 */
type PendingRequest = {
  threadId: string;
  resolve: (confirmed: boolean) => void;
};

type State = {
  pending: PendingRequest | null;
  request: (threadId: string) => Promise<boolean>;
  resolve: (confirmed: boolean) => void;
};

export const useAnonymizationSendGate = create<State>((set, get) => ({
  pending: null,
  request: (threadId) =>
    new Promise<boolean>((resolve) => {
      const prior = get().pending;
      if (prior) prior.resolve(false);
      set({ pending: { threadId, resolve } });
    }),
  resolve: (confirmed) => {
    const p = get().pending;
    if (!p) return;
    p.resolve(confirmed);
    set({ pending: null });
  },
}));
